import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { RouteNameEnum } from '@connect-services/client/models/routes.model';

enum FaqAnchorKeyEnum {
    AUTH = 'auth',
    DATA = 'data',
    FIELD_INTERVENTION = 'fieldIntervention',
    MAP = 'map',
    PROJECTS = 'projects',
    STATS = 'stats',
    SYNOPTIC = 'synoptic',
    USER_ROLE = 'userRole',
}

@Injectable({
    providedIn: 'root',
})
export class FaqService {
    private BASE_URL = {
        en: 'https://connectservices.io/en/faq-clients-en/',
        fr: 'https://connectservices.io/faq-clients/',
    };
    private FAQ_ANCHOR = {
        en: {
            [FaqAnchorKeyEnum.AUTH]: 'Sign-in & user profile',
            [FaqAnchorKeyEnum.DATA]: 'Data and repositories',
            [FaqAnchorKeyEnum.FIELD_INTERVENTION]: 'Field operations',
            [FaqAnchorKeyEnum.MAP]: 'Map navigation and tools',
            [FaqAnchorKeyEnum.PROJECTS]: 'Projects',
            [FaqAnchorKeyEnum.STATS]: 'Data statistics and visualization',
            [FaqAnchorKeyEnum.SYNOPTIC]: 'Box Plan, and Optical Route',
            [FaqAnchorKeyEnum.USER_ROLE]: 'User and Roles',
        },
        fr: {
            [FaqAnchorKeyEnum.AUTH]: 'Connexion et profil',
            [FaqAnchorKeyEnum.DATA]: 'Données et dépôts',
            [FaqAnchorKeyEnum.FIELD_INTERVENTION]: 'Interventions terrain',
            [FaqAnchorKeyEnum.MAP]: 'Navigation cartographique et outils',
            [FaqAnchorKeyEnum.PROJECTS]: 'Projets',
            [FaqAnchorKeyEnum.STATS]: 'Statistiques et visualisation',
            [FaqAnchorKeyEnum.SYNOPTIC]: 'Plan de boite et Route optique',
            [FaqAnchorKeyEnum.USER_ROLE]: 'Gestion des utilisateurs et rôles',
        },
    };
    private readonly router = inject(Router);
    private translocoService = inject(TranslocoService);

    public redirect(): void {
        window.open(this.buildUrl(), '_blank', 'noopener,noreferrer');
    }

    private buildUrl(): string {
        const textAnchor = '#:~:text=';
        const anchorValue = this.getAnchorValue();

        return `${this.getBaseURL()}${textAnchor}${encodeURI(anchorValue)}`;
    }

    private getAnchors(key: FaqAnchorKeyEnum): string {
        switch (this.translocoService.getActiveLang()) {
            case 'fr':
                return this.FAQ_ANCHOR.fr[key];
            case 'en':
            default:
                return this.FAQ_ANCHOR.en[key];
        }
    }

    private getAnchorValue(): string {
        const url = this.router.url;

        switch (true) {
            case url.includes(RouteNameEnum.FIELD_INTERVENTIONS):
                return this.getAnchors(FaqAnchorKeyEnum.FIELD_INTERVENTION);

            case url.includes(RouteNameEnum.STATISTICS):
                return this.getAnchors(FaqAnchorKeyEnum.STATS);

            case url.includes(RouteNameEnum.SETTINGS):
                return this.getAnchors(FaqAnchorKeyEnum.PROJECTS);

            case url.includes(RouteNameEnum.SYNOPTIC):
                return this.getAnchors(FaqAnchorKeyEnum.SYNOPTIC);

            case url.includes(RouteNameEnum.PROJECTS):
                return this.getAnchors(FaqAnchorKeyEnum.MAP);

            case url.includes(RouteNameEnum.MANAGEMENT_DATA):
                return this.getAnchors(FaqAnchorKeyEnum.DATA);

            case url.includes(RouteNameEnum.USERS_ROLES):
                return this.getAnchors(FaqAnchorKeyEnum.USER_ROLE);

            case url.includes(RouteNameEnum.LOGIN):
                return this.getAnchors(FaqAnchorKeyEnum.AUTH);

            default:
                return '';
        }
    }

    private getBaseURL(): string {
        switch (this.translocoService.getActiveLang()) {
            case 'fr':
                return this.BASE_URL.fr;
            case 'en':
            default:
                return this.BASE_URL.en;
        }
    }
}
